import React, { useContext, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import { Navigation } from '../components/Navigation';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Trophy, DollarSign, Users, MapPin, Video, Image, Hash, Save, Upload, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function PostStory() {
  const { user, loading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [mediaPreview, setMediaPreview] = useState(null);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    winner_name: '',
    amount_won: '',
    course_name: '',
    players: '',
    description: '',
    media_url: '',
    media_type: 'image',
    hashtags: ''
  });

  React.useEffect(() => {
    if (!loading && !user) {
      navigate('/');
    }
    // Redirect non-members to membership page
    if (!loading && user && !user.is_member) {
      toast.error('Active membership required to post stories');
      navigate('/membership');
    }
  }, [loading, user, navigate]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/quicktime', 'video/webm'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select an image (JPEG, PNG, GIF, WebP) or video (MP4, MOV, WebM)');
      return;
    }

    // Validate file size — images 50MB, videos up to 500MB
    const isVid = file.type.startsWith('video/');
    const cap = isVid ? 500 * 1024 * 1024 : 50 * 1024 * 1024;
    if (file.size > cap) {
      const mb = (file.size / 1024 / 1024).toFixed(0);
      toast.error(`File too large (${mb}MB). Max size is ${cap / 1024 / 1024}MB for ${isVid ? 'videos' : 'images'}.`);
      return;
    }

    // Create preview
    const previewUrl = URL.createObjectURL(file);
    const isVideo = file.type.startsWith('video/');
    setMediaPreview({ url: previewUrl, type: isVideo ? 'video' : 'image', name: file.name });

    // Upload file
    setUploading(true);
    setUploadProgress(0);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(`${API}/upload/media`, {
        method: 'POST',
        credentials: 'include',
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({
          ...prev,
          media_url: data.media_url,
          media_type: data.media_type
        }));
        setUploadProgress(100);
        toast.success('Media uploaded successfully!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload file');
        clearMedia();
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file');
      clearMedia();
    } finally {
      setUploading(false);
    }
  };

  const clearMedia = () => {
    if (mediaPreview?.url) {
      URL.revokeObjectURL(mediaPreview.url);
    }
    setMediaPreview(null);
    setFormData(prev => ({ ...prev, media_url: '', media_type: 'image' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user.is_member) {
      toast.error('Active membership required to post stories');
      navigate('/membership');
      return;
    }

    if (!formData.winner_name || !formData.amount_won || !formData.course_name || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API}/stories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          winner_name: formData.winner_name,
          amount_won: parseFloat(formData.amount_won),
          course_name: formData.course_name,
          players: formData.players ? formData.players.split(',').map(p => p.trim()) : [],
          description: formData.description,
          media_url: formData.media_url || null,
          media_type: formData.media_type,
          hashtags: formData.hashtags ? formData.hashtags.split(',').map(h => h.trim().replace('#', '')) : []
        })
      });

      if (response.ok) {
        toast.success('Bundle story posted!');
        navigate('/stories');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to post story');
      }
    } catch (error) {
      toast.error('An error occurred');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bundle-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-bundle-green"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bundle-cream pb-mobile-nav md:pb-8">
      <Navigation user={user} />

      <div className="max-w-2xl mx-auto px-4 md:px-8 pt-20 md:pt-24">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/stories')}
          className="mb-4 text-bundle-green hover:bg-bundle-cream-dark -ml-2"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Stories
        </Button>

        <div className="mb-6">
          <h1 className="font-heading font-bold text-3xl text-bundle-green mb-2">Post Bundle Story</h1>
          <p className="font-body text-gray-600">Share who won the bundle today!</p>
        </div>

        <Card className="bg-white border-bundle-cream-dark">
          <CardContent className="p-6 md:p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Winner & Amount */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-body text-bundle-green flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-bundle-gold" />
                    Winner Name *
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      data-testid="winner-name-input"
                      placeholder="Who won the bundle?"
                      value={formData.winner_name}
                      onChange={(e) => setFormData({ ...formData, winner_name: e.target.value })}
                      className="border-bundle-cream-dark focus:border-bundle-green focus:ring-bundle-green flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setFormData({ ...formData, winner_name: user.name })}
                      className="border-bundle-gold text-bundle-gold hover:bg-bundle-gold hover:text-white whitespace-nowrap"
                    >
                      I Won!
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="font-body text-bundle-green flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-green-600" />
                    Amount Won *
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    data-testid="amount-won-input"
                    placeholder="e.g., 50"
                    value={formData.amount_won}
                    onChange={(e) => setFormData({ ...formData, amount_won: e.target.value })}
                    className="border-bundle-cream-dark focus:border-bundle-green focus:ring-bundle-green"
                  />
                </div>
              </div>

              {/* Course */}
              <div className="space-y-2">
                <Label className="font-body text-bundle-green flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Course Name *
                </Label>
                <Input
                  data-testid="course-name-input"
                  placeholder="Where did you play?"
                  value={formData.course_name}
                  onChange={(e) => setFormData({ ...formData, course_name: e.target.value })}
                  className="border-bundle-cream-dark focus:border-bundle-green focus:ring-bundle-green"
                />
              </div>

              {/* Players */}
              <div className="space-y-2">
                <Label className="font-body text-bundle-green flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Other Players (Optional)
                </Label>
                <Input
                  data-testid="players-input"
                  placeholder="Comma separated: John, Mike, Sarah"
                  value={formData.players}
                  onChange={(e) => setFormData({ ...formData, players: e.target.value })}
                  className="border-bundle-cream-dark focus:border-bundle-green focus:ring-bundle-green"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label className="font-body text-bundle-green">Description *</Label>
                <Textarea
                  data-testid="description-input"
                  placeholder="Tell the story of the win..."
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="border-bundle-cream-dark focus:border-bundle-green focus:ring-bundle-green"
                />
              </div>

              {/* Media Upload */}
              <div className="space-y-3">
                <Label className="font-body text-bundle-green flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  Photo or Video (Optional)
                </Label>
                
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="media-file-input"
                />

                {/* Upload area or preview */}
                {!mediaPreview ? (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-bundle-cream-dark rounded-xl p-8 text-center cursor-pointer hover:border-bundle-green hover:bg-bundle-cream/50 transition-colors"
                  >
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-14 h-14 bg-bundle-green/10 rounded-full flex items-center justify-center">
                        <Upload className="w-7 h-7 text-bundle-green" />
                      </div>
                      <div>
                        <p className="font-body font-medium text-bundle-green">Click to upload</p>
                        <p className="font-body text-sm text-gray-500 mt-1">
                          JPEG, PNG, GIF, WebP, MP4, MOV, WebM (max 50MB)
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="relative border border-bundle-cream-dark rounded-xl overflow-hidden bg-gray-50">
                    {/* Preview */}
                    <div className="aspect-video relative">
                      {mediaPreview.type === 'video' ? (
                        <video 
                          src={mediaPreview.url} 
                          className="w-full h-full object-contain bg-black"
                          controls
                        />
                      ) : (
                        <img 
                          src={mediaPreview.url} 
                          alt="Preview" 
                          className="w-full h-full object-contain"
                        />
                      )}
                      
                      {/* Upload progress overlay */}
                      {uploading && (
                        <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center">
                          <Loader2 className="w-10 h-10 text-white animate-spin mb-2" />
                          <p className="text-white font-body text-sm">Uploading...</p>
                        </div>
                      )}
                    </div>
                    
                    {/* File info and remove button */}
                    <div className="p-3 flex items-center justify-between bg-white border-t border-bundle-cream-dark">
                      <div className="flex items-center gap-2">
                        {mediaPreview.type === 'video' ? (
                          <Video className="w-4 h-4 text-bundle-green" />
                        ) : (
                          <Image className="w-4 h-4 text-bundle-green" />
                        )}
                        <span className="font-body text-sm text-gray-600 truncate max-w-[200px]">
                          {mediaPreview.name}
                        </span>
                        {formData.media_url && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                            Uploaded
                          </span>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={clearMedia}
                        disabled={uploading}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Remove
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Hashtags */}
              <div className="space-y-2">
                <Label className="font-body text-bundle-green flex items-center gap-2">
                  <Hash className="w-4 h-4" />
                  Hashtags (Optional)
                </Label>
                <Input
                  data-testid="hashtags-input"
                  placeholder="moneygame, birdie, thebundle"
                  value={formData.hashtags}
                  onChange={(e) => setFormData({ ...formData, hashtags: e.target.value })}
                  className="border-bundle-cream-dark focus:border-bundle-green focus:ring-bundle-green"
                />
                <p className="font-body text-xs text-gray-500">Comma separated, without # symbol</p>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                data-testid="submit-story-btn"
                disabled={submitting}
                className="w-full btn-bundle py-6"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                    Posting...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5 mr-2" />
                    Post Story
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
